import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { rememberPendingRegistration } from '../lib/pendingRegistration';
import styles from './LoginPage.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Account creation goes through the public registration form (/register), so this
  // page is sign-in only. Any legacy "signup" intent is redirected there.
  const [mode]     = useState('login');
  const [email,    setEmail]    = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState(() => {
    if (location.state?.verified) return 'Your email is verified. Sign in to continue.';
    if (location.state?.registered) return 'Account created! Please sign in to continue.';
    return '';
  });
  // Official logo at /logo.png; fall back to the lettermark if it's missing.
  const [logoOk,   setLogoOk]   = useState(true);

  useEffect(() => {
    if (location.state?.mode === 'signup') navigate('/register', { replace: true });
  }, [location.state, navigate]);

  // Warm the backend while the user types their credentials, so the post-login
  // access-status check isn't waiting on a cold server. Fire-and-forget.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/health`).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    if (!supabase) {
      setError('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to frontend/.env');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      // Both unverified branches below hand the password over (memory only - see
      // lib/pendingRegistration), because verifying sends it with the code. Without it the
      // verification page would have to ask for the password the user has just typed.
      // No code has been sent on the way there, and the navigation state says nothing
      // about one, so the page opens without a countdown and with "Resend code" ready.
      if (error) {
        // An unverified account (SCRUM-49) can fail here rather than sign in, depending on
        // whether GoTrue is configured to refuse unconfirmed addresses. Both configurations
        // have to land the user in the same place, so the error is recognised and routed
        // instead of being shown as a dead end they cannot act on.
        if (/not confirmed|email.*confirm/i.test(error.message || '')) {
          rememberPendingRegistration({ email, password });
          navigate('/verify-email', { state: { email } });
          return;
        }
        setError(error.message.includes('Invalid login') ? 'Incorrect email or password.' : error.message);
      } else {
        // The other configuration: the sign-in succeeded and produced a session for an
        // address that was never confirmed. ProtectedRoute would bounce them off /app
        // anyway - going straight to verification skips a redirect the user would see.
        const signedIn = data?.user;
        if (signedIn && !(signedIn.email_confirmed_at || signedIn.confirmed_at)) {
          rememberPendingRegistration({ email, password });
          navigate('/verify-email', { state: { email } });
          return;
        }
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Logo - doubles as a link back to the marketing home */}
        <button className={styles.logo} onClick={() => navigate('/')} aria-label="RepBuddy home">
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
        </button>

        <p className={styles.tagline}>
          Sign in to access your agents
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Email
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
              disabled={loading}
            />
          </label>

          <label className={styles.label}>
            Password
            <input
              className={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </label>

          {error && <div className={styles.error}>{error}</div>}
          {info  && <div className={styles.info}>{info}</div>}

          <button className={styles.btn} type="submit" disabled={loading || !email || !password}>
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>

          {/* Carries the typed address across so the reset page does not ask for it twice -
              someone who just failed to sign in has already told us who they are. type="button"
              matters: inside a <form>, a bare <button> submits it. */}
          <div className={styles.forgotRow}>
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate('/forgot-password', { state: { email } })}
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>
        </form>

        {/* Toggle - public sign-ups happen on the registration page (/register) */}
        <p className={styles.toggle}>
          Don't have an account?{' '}
          <button className={styles.link} onClick={() => navigate('/register')}>
            Register
          </button>
        </p>

        <button className={styles.backHome} onClick={() => navigate('/')}>
          ⬅️ Back to home
        </button>

      </div>
    </div>
  );
}
